/* eslint-disable no-undef */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { useIsLoggedInQuery } from '../../redux/apis/authApi';
import { LB_HEADER } from '../../utils/constant/labels';
import { GLOBAL, NOTIFICATION_TYPES_KEYS } from '../../utils/constant/global';
import { getRelativeTime, handleApiError, skipRow, unique } from '../../utils/handler';
import {
  useGetHRXNotificationQuery,
  useUpdateNotificationReadStatusMutation
} from '../../redux/apis/Notification';

import './SidebarNotifications.scss';

const { DESC, ASC } = GLOBAL;
const { LB_NOTIFICATION } = LB_HEADER;

const buildInitialState = (rowsPerPage) => ({
  currentPage: 1,
  rowsPerPage,
  isNext: false,
  sortField: 'time',
  sortDirection: true,
  loading: false,
  notificationList: [],
  showDropdown: false
});

const NOTIFICATION_TYPE_META = {
  [NOTIFICATION_TYPES_KEYS.NT_SUPPORT_TICKET]: { icon: 'icofont-ticket', tone: 'violet' },
  [NOTIFICATION_TYPES_KEYS.NT_LEAVE_REQUEST]: { icon: 'icofont-beach-bed', tone: 'teal' },
  [NOTIFICATION_TYPES_KEYS.NT_ANNOUNCEMENT]: { icon: 'icofont-megaphone', tone: 'amber' },
  [NOTIFICATION_TYPES_KEYS.NT_HOLIDAY]: { icon: 'icofont-gift', tone: 'rose' },
  [NOTIFICATION_TYPES_KEYS.NT_PERFORMANCE_REVIEW]: { icon: 'icofont-chart-growth', tone: 'indigo' }
};

const getTypeMeta = (type) =>
  NOTIFICATION_TYPE_META[type] || { icon: 'icofont-bell-alt', tone: 'slate' };

/**
 * Notification bell + dropdown (toolbar) or last sidebar module row (menuItem).
 */
function SidebarNotifications({ isSidebarMini, variant = 'toolbar' }) {
  const isMenuItem = variant === 'menuItem';
  const pageSize = isMenuItem ? 20 : 5;

  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [state, setState] = useState(() => buildInitialState(pageSize));

  const { data } = useIsLoggedInQuery();
  const values = data?.data;

  const {
    data: notificationData,
    refetch: notificationFetch,
    isFetching: notificationFetching
  } = useGetHRXNotificationQuery(
    {
      search: state?.search || '',
      skip: skipRow(state?.currentPage, state?.rowsPerPage),
      limit: state?.rowsPerPage,
      sort: state?.sortField,
      sortType: state.sortDirection ? DESC : ASC,
      userId: values?._id
    },
    { skip: !values?._id }
  );

  const [updateNotificationReadStatus] = useUpdateNotificationReadStatusMutation();

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setState((prev) => ({ ...prev, showDropdown: false }));
    }
  };

  useEffect(() => {
    if (values?._id) {
      notificationFetch();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [values?._id]);

  useEffect(() => {
    if (notificationData?.data?.notificationList?.length > 0) {
      const newArray = [
        ...state.notificationList,
        ...(notificationData?.data?.notificationList || [])
      ];
      const finalArray = unique(newArray, '_id');
      setState((prev) => ({
        ...prev,
        isNext: notificationData?.isNext,
        notificationList: finalArray
      }));
    }
  }, [notificationData]);

  const onNotificationLoadInitial = useCallback(() => {
    const newArray = [
      ...state.notificationList,
      ...(notificationData?.data?.notificationList || [])
    ];
    const finalArray = unique(newArray, '_id');
    setState((prev) => ({
      ...prev,
      isNext: notificationData?.isNext,
      notificationList: finalArray
    }));
  }, [notificationData, state.notificationList]);

  const onNotificationLoad = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentPage: prev.isNext ? prev.currentPage + 1 : prev.currentPage
    }));
  }, []);

  useEffect(() => {
    if (values?._id) {
      notificationFetch();
    }
  }, [state.currentPage, state.rowsPerPage, values?._id]);

  useEffect(() => {
    setState((prev) =>
      prev.rowsPerPage === pageSize
        ? prev
        : { ...buildInitialState(pageSize), showDropdown: prev.showDropdown }
    );
  }, [pageSize]);

  const handleReadStatus = async (routeUrl, id) => {
    const { data: resData, error } = await updateNotificationReadStatus({ id });

    if (resData?.success) {
      setState((prev) => ({ ...prev, notificationList: [], showDropdown: false }));
      notificationFetch();
      navigate(routeUrl);
    } else if (error) {
      setState((prev) => ({ ...prev, notificationList: [], showDropdown: false }));
      handleApiError(error);
    }
  };

  const handleNavigate = (notification) => {
    let routeUrl = '';
    switch (notification.data?.type) {
      case NOTIFICATION_TYPES_KEYS.NT_SUPPORT_TICKET:
        routeUrl = '/support-ticket';
        break;
      case NOTIFICATION_TYPES_KEYS.NT_LEAVE_REQUEST:
        routeUrl = '/leave-request';
        break;
      case NOTIFICATION_TYPES_KEYS.NT_ANNOUNCEMENT:
        routeUrl = '/announcement';
        break;
      case NOTIFICATION_TYPES_KEYS.NT_HOLIDAY:
        routeUrl = '/holidays';
        break;
      case NOTIFICATION_TYPES_KEYS.NT_PERFORMANCE_REVIEW:
        routeUrl = '/profile';
        break;
      default:
        break;
    }
    handleReadStatus(routeUrl, notification._id);
  };

  const unread = notificationData?.data?.totalUnReadCount ?? 0;
  const totalCount = notificationData?.data?.totalCount ?? notificationData?.totalCount ?? 0;
  const sideCount =
    unread > 0
      ? unread > 99
        ? '99+'
        : String(unread)
      : totalCount > 0
        ? totalCount > 99
          ? '99+'
          : String(totalCount)
        : null;
  const sideCountIsUnread = unread > 0;

  if (!values?._id) {
    return null;
  }

  return (
    <div
      className={`sidebar-notifications ${isSidebarMini ? 'sidebar-notifications--mini' : ''} ${
        isMenuItem ? 'sidebar-notifications--menu-item' : ''
      } ${state.showDropdown && isMenuItem ? 'is-open' : ''}`}
      ref={dropdownRef}
    >
      <Dropdown
        show={state.showDropdown}
        className="sidebar-notifications__dropdown w-100"
        drop={isMenuItem ? 'up' : 'down'}
        align="start"
        popperConfig={{ strategy: 'fixed' }}
        onToggle={(next) => {
          if (next) {
            onNotificationLoadInitial();
          }
          setState((prev) => ({ ...prev, showDropdown: next }));
        }}
      >
        <Dropdown.Toggle
          as="button"
          type="button"
          variant="link"
          id="sidebar-notifications-toggle"
          className={`sidebar-notifications__toggle ${isMenuItem ? 'sidebar-notifications__toggle--menu-item' : ''}`}
          aria-label={LB_NOTIFICATION}
        >
          <i className="icofont-alarm sidebar-notifications__bell">
            {isMenuItem && isSidebarMini && sideCount ? (
              <span
                className={`sidebar-notifications__badge ${sideCountIsUnread ? '' : 'sidebar-notifications__badge--total'}`}
              >
                {sideCount}
              </span>
            ) : !isMenuItem && unread > 0 ? (
              <span className="sidebar-notifications__badge">{unread > 99 ? '99+' : unread}</span>
            ) : null}
          </i>
          {!isSidebarMini ? (
            <span className="sidebar-notifications__label">{LB_NOTIFICATION}</span>
          ) : null}
          {isMenuItem && !isSidebarMini && sideCount ? (
            <span
              className={`sidebar-notifications__side-count ${
                sideCountIsUnread
                  ? 'sidebar-notifications__side-count--unread'
                  : 'sidebar-notifications__side-count--total'
              }`}
              aria-label={
                sideCountIsUnread ? `${unread} unread notifications` : `${totalCount} notifications`
              }
            >
              {sideCount}
            </span>
          ) : null}
        </Dropdown.Toggle>

        <Dropdown.Menu className="sidebar-notifications__menu shadow border-0 p-0">
          <div className="sidebar-notifications__panel">
            <header className="sidebar-notifications__panel-head">
              <div>
                <h6 className="sidebar-notifications__panel-title">{LB_NOTIFICATION}</h6>
                <p className="sidebar-notifications__panel-sub">
                  {state.notificationList?.length > 0
                    ? `${totalCount || state.notificationList.length} total${
                        unread > 0 ? ` · ${unread} unread` : ''
                      }`
                    : 'Stay updated on leave, news, and more'}
                </p>
              </div>
              {unread > 0 ? (
                <span className="sidebar-notifications__panel-badge">
                  {unread > 99 ? '99+' : unread}
                </span>
              ) : null}
            </header>

            <div className="sidebar-notifications__list-wrap">
              <ul className="sidebar-notifications__list">
                {state.notificationList?.length > 0 ? (
                  state.notificationList.map((notification) => {
                    const isRead = Boolean(notification?.readStatus);
                    const meta = getTypeMeta(notification?.data?.type);
                    return (
                      <li key={notification._id}>
                        <button
                          type="button"
                          className={`sidebar-notifications__row ${isRead ? 'is-read' : 'is-unread'}`}
                          onClick={() => handleNavigate(notification)}
                        >
                          <span
                            className={`sidebar-notifications__type-icon sidebar-notifications__type-icon--${meta.tone}`}
                            aria-hidden
                          >
                            <i className={`icofont ${meta.icon}`} />
                          </span>
                          <span className="sidebar-notifications__row-main">
                            <span className="sidebar-notifications__row-title">
                              {notification?.title || 'Notification'}
                            </span>
                            {notification?.body ? (
                              <span className="sidebar-notifications__row-body">
                                {notification.body}
                              </span>
                            ) : null}
                          </span>
                          <time
                            className="sidebar-notifications__row-time"
                            dateTime={notification?.time}
                          >
                            {notification?.time ? getRelativeTime(notification.time) : ''}
                          </time>
                        </button>
                      </li>
                    );
                  })
                ) : (
                  <li className="sidebar-notifications__empty">
                    <span className="sidebar-notifications__empty-icon" aria-hidden>
                      <i className="icofont icofont-inbox" />
                    </span>
                    <p className="sidebar-notifications__empty-title">You&apos;re all caught up</p>
                    <p className="sidebar-notifications__empty-text">
                      New alerts will show up here.
                    </p>
                  </li>
                )}
                {state.loading ? (
                  <li className="sidebar-notifications__loading">
                    <Spinner animation="border" size="sm" className="text-primary" />
                  </li>
                ) : null}
              </ul>
            </div>

            <footer className="sidebar-notifications__footer">
              {state.notificationList?.length > 0 ? (
                <button
                  type="button"
                  className="sidebar-notifications__load-more"
                  onClick={() => onNotificationLoad()}
                  disabled={!state.isNext || notificationFetching}
                >
                  {notificationFetching ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading…
                    </>
                  ) : (
                    'Load more'
                  )}
                </button>
              ) : null}
              <button
                type="button"
                className="sidebar-notifications__view-all"
                onClick={() => {
                  setState((prev) => ({ ...prev, showDropdown: false }));
                  navigate('/notifications');
                }}
              >
                View all notifications
              </button>
            </footer>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

export default SidebarNotifications;
